import Konva from "konva";
import type { Bounds } from "./metadata-bounds";

function overlapArea(a: Bounds, b: Bounds) {
  const width = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const height = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  return width * height;
}

const sansFont = "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
const monoFont = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

export function createMetadataCornerReadout(stage: Konva.Stage, layer: Konva.Layer) {
  const card = new Konva.Group({ listening: false, visible: false });
  const background = new Konva.Rect({
    fill: "#ffffff",
    stroke: "#d4d4d4",
    strokeWidth: 1,
    cornerRadius: 4,
  });
  const title = new Konva.Text({
    x: 12,
    y: 9,
    fontSize: 12,
    fontStyle: "600",
    fontFamily: sansFont,
    fill: "#171717",
    wrap: "none",
    ellipsis: true,
  });
  const context = new Konva.Text({
    x: 12,
    y: 26,
    fontSize: 11,
    fontFamily: sansFont,
    fill: "#737373",
    wrap: "none",
    ellipsis: true,
  });
  const divider: Konva.Line = new Konva.Line({ stroke: "#e5e5e5", strokeWidth: 1 });
  const coordinates = ["X", "Y"].map((axis) => {
    const label = new Konva.Text({ text: axis, fontFamily: monoFont, fontSize: 10, fontStyle: "600", fill: "#a3a3a3" });
    const value = new Konva.Text({
      fontFamily: monoFont,
      fontSize: 11,
      fontStyle: "500",
      fill: "#171717",
      wrap: "none",
      ellipsis: true,
    });
    return { label, value };
  });
  card.add(background, title, context, divider);
  coordinates.forEach(({ label, value }) => card.add(label, value));
  layer.add(card);

  let corner = 0;
  let width = 224;
  let height = 72;
  let obstacles: Bounds[] = [];
  const place = () => {
    if (!card.visible()) return;
    const margin = 8;
    const right = stage.width() - width - margin;
    const bottom = stage.height() - height - margin;
    const corners = [
      { x: right, y: bottom },
      { x: margin, y: bottom },
    ];
    const pointer = stage.getPointerPosition();
    const cursor = pointer ? { x: pointer.x - 16, y: pointer.y - 16, width: 32, height: 32 } : null;
    const scores = corners.map((position) => {
      const bounds = { ...position, width, height };
      const coveredArea = obstacles.reduce((area, obstacle) => area + overlapArea(bounds, obstacle), 0);
      const cursorPenalty =
        cursor && overlapArea(bounds, cursor) > 0 ? stage.width() * stage.height() * (obstacles.length + 1) : 0;
      return coveredArea + cursorPenalty;
    });
    const best = scores.indexOf(Math.min(...scores));
    const currentScore = scores[corner]!;
    const bestScore = scores[best]!;
    if (bestScore === 0 || currentScore - bestScore > 144) {
      if (currentScore > bestScore) corner = best;
    }
    const position = corners[corner]!;
    const scale = stage.scaleX() || 1;
    card.position({ x: position.x / scale, y: position.y / scale });
    card.scale({ x: 1 / scale, y: 1 / scale });
  };
  stage.on("mousemove.metadataReadout", place);

  return {
    hide: () => card.hide(),
    update: (name: string, detail: string, point: number[], bounds: Bounds[], warning: boolean) => {
      width = Math.min(224, stage.width() - 16);
      height = Math.min(72, stage.height() - 16);
      if (width < 100 || height < 28) {
        card.hide();
        return;
      }
      const scale = stage.scaleX() || 1;
      obstacles = bounds.map((bounds) => ({
        x: bounds.x * scale - 12,
        y: bounds.y * scale - 12,
        width: bounds.width * scale + 24,
        height: bounds.height * scale + 24,
      }));
      card.show();
      card.clip({ x: 0, y: 0, width, height });
      background.size({ width, height });
      title.setAttrs({ text: name, width: width - 24, visible: height >= 48 });
      context.setAttrs({
        text: detail,
        width: width - 24,
        visible: height >= 68,
        fill: warning ? "#b45309" : "#737373",
      });
      divider.setAttrs({ points: [12, height - 29, width - 12, height - 29], visible: height >= 68 });
      coordinates.forEach(({ label, value }, index) => {
        const x = 12 + (index * (width - 24)) / 2;
        const y = height < 48 ? (height - 11) / 2 : height - 21;
        label.position({ x, y: y + 1 });
        value.setAttrs({ x: x + 18, y, width: (width - 24) / 2 - 22, text: String(Math.round(point[index]!)) });
      });
      place();
    },
    dispose: () => {
      stage.off(".metadataReadout");
      card.destroy();
    },
  };
}
